import { useState, useCallback } from 'react'
import { INewContact } from 'apps/fleet-planner/schema/ContactSchema'
import * as Types from 'apps/fleet-planner/types'
import * as Api from 'apps/fleet-planner/utils/api'

interface IUseCustomerContactApiState {
  error?: string
  isLoading: boolean
  resetError: () => void
  /**
   * PUT request to update a contact
   */
  updateCustomerContact: (
    customerId: string,
    contactId: string,
    contact: INewContact
  ) => Promise<Types.ICustomerContact | undefined>
  /**
   * POST request to create new contact
   */
  createNewCustomerContact: (
    customerId: string,
    contact: Pick<Types.ICustomerContact, 'email' | 'name' | 'phone' | 'phoneExt' | 'purpose'>
  ) => Promise<Types.ICustomerContact | undefined>
}

/**
 * Hook used to create/update contact entities
 */
export const useCustomerContactApi = (): IUseCustomerContactApiState => {
  const [isLoading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')

  const resetError = useCallback(() => setError(''), [])

  /**
   * Create new customer contact entity
   */
  const createNewCustomerContact = useCallback(
    async (
      customerId: string,
      c: Pick<Types.ICustomerContact, 'email' | 'name' | 'phone' | 'phoneExt' | 'purpose'>
    ): Promise<Types.ICustomerContact | undefined> => {
      setLoading(true)
      try {
        const { contact } = await Api.createNewCustomerContact(customerId, c)
        setLoading(false)
        setError('')
        return contact
      } catch (e) {
        setError(e)
        setLoading(false)
        return undefined
      }
    },
    []
  )

  /**
   * Create new customer contact entity
   */
  const updateCustomerContact = useCallback(
    async (
      customerId: string,
      contactId: string,
      c: INewContact
    ): Promise<Types.ICustomerContact | undefined> => {
      setLoading(true)
      try {
        const { contact } = await Api.updateCustomerContact(customerId, contactId, c)
        setLoading(false)
        setError('')
        return contact
      } catch (e) {
        setError(e)
        setLoading(false)
        return undefined
      }
    },
    []
  )

  return {
    createNewCustomerContact,
    updateCustomerContact,
    error,
    resetError,
    isLoading,
  }
}
